/**
 * Checkout Success Page
 *
 * Shown after successful Stripe checkout (subscription or purchase).
 * Confirms payment, provides order details, and next steps.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import navigationService from '../services/navigationService';
import './CheckoutSuccess.css';

const CheckoutSuccess = () => {
  const { user, isSubscriber, refreshUser } = useAuth();
  const { clearCart } = useCart();
  const [orderType, setOrderType] = useState(null); // 'subscription' or 'purchase'

  useEffect(() => {
    // Check URL parameters to determine what was purchased
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const type = params.get('type'); // 'subscription' or 'purchase'

    if (type) {
      setOrderType(type);
    }

    // Clear cart if it was a purchase
    if (type === 'purchase') {
      clearCart();
    }

    // Refresh user data to get updated subscription status
    if (refreshUser) {
      refreshUser();
    }

    // Log success for analytics
    console.log('✅ Checkout successful:', { sessionId, type });
  }, [clearCart, refreshUser]);

  const handleContinueShopping = () => {
    navigationService.navigateTo('home');
  };

  const handleViewAccount = () => {
    // TODO: Navigate to account dashboard when built
    console.log('Navigate to account dashboard');
  };

  return (
    <>
      <MainHeader currentPage="checkout-success" />
      <div className="checkout-success">
        <div className="success-container">
          {/* Success Icon */}
          <div className="success-icon">
            <MaterialIcon icon="check_circle" size={80} />
          </div>

          {/* Success Message */}
          <h1 className="success-title">Payment Successful!</h1>

          {orderType === 'subscription' ? (
            // Subscription Success
            <>
              <p className="success-subtitle">Welcome to the Sticker of the Month Club! 🎉</p>

              <div className="success-details">
                <h2>What Happens Next?</h2>
                <div className="success-steps">
                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="mail" />
                    </div>
                    <div className="step-content">
                      <h3>Confirmation Email</h3>
                      <p>Check your inbox for order confirmation and receipt</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="local_shipping" />
                    </div>
                    <div className="step-content">
                      <h3>First Sticker Ships Soon</h3>
                      <p>Your exclusive sticker will arrive within 5-7 business days</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="how_to_vote" />
                    </div>
                    <div className="step-content">
                      <h3>Vote on Next Design</h3>
                      <p>Help choose next month's exclusive sticker design</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="emoji_events" />
                    </div>
                    <div className="step-content">
                      <h3>Enter Monthly Contest</h3>
                      <p>Submit a scene for a chance to win a free custom print</p>
                    </div>
                  </div>
                </div>

                <div className="success-subscriber-badge">
                  <MaterialIcon icon="verified" />
                  <span>You're now a ClothedEye Subscriber!</span>
                </div>
              </div>
            </>
          ) : (
            // Purchase Success
            <>
              <p className="success-subtitle">
                Your order has been confirmed and is being processed.
              </p>

              <div className="success-details">
                <h2>What Happens Next?</h2>
                <div className="success-steps">
                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="mail" />
                    </div>
                    <div className="step-content">
                      <h3>Confirmation Email</h3>
                      <p>Check your inbox for order confirmation and receipt</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="inventory" />
                    </div>
                    <div className="step-content">
                      <h3>Order Processing</h3>
                      <p>We'll prepare your stickers for shipment</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="local_shipping" />
                    </div>
                    <div className="step-content">
                      <h3>Shipping Notification</h3>
                      <p>You'll receive tracking info when your order ships</p>
                    </div>
                  </div>

                  <div className="success-step">
                    <div className="step-icon">
                      <MaterialIcon icon="celebration" />
                    </div>
                    <div className="step-content">
                      <h3>Delivery</h3>
                      <p>Your stickers arrive within 5-7 business days</p>
                    </div>
                  </div>
                </div>

                <div className="success-thank-you">
                  <MaterialIcon icon="favorite" />
                  <span>Thank you for supporting ClothedEye directly!</span>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="success-actions">
            <button className="success-button-primary" onClick={handleContinueShopping}>
              <MaterialIcon icon="storefront" />
              Continue Shopping
            </button>

            {user && (
              <button className="success-button-secondary" onClick={handleViewAccount}>
                <MaterialIcon icon="account_circle" />
                View My Account
              </button>
            )}
          </div>

          {/* Additional Info */}
          <div className="success-info">
            <p>
              <MaterialIcon icon="help" />
              Questions? Email us at support@clothedeye.com
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutSuccess;
